import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LockKeyhole, ArrowRight, Palette, CalendarCheck } from "lucide-react";

import { artists } from "@/data/artists";
import { useCatalogVersion } from "@/data/catalogLive";
import AdminHeader from "@/components/latir/AdminHeader";
import { useSEO } from "@/hooks/useSEO";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "arteconciencia_admin_token_v1";

export default function AdminHome() {
  useCatalogVersion();
  useSEO({ title: "Panel administrativo | Arteconciencia", noindex: true });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  if (!token) {
    return (
      <Login
        onLogin={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }
  return <Dashboard token={token} />;
}

/* ---------------- Login ---------------- */
function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/visits/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Contraseña incorrecta");
      onLogin(data.token);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="paper-grain min-h-screen bg-paper flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm text-center" data-testid="admin-home-login">
        <p className="eyebrow text-latir-red mb-6">Arteconciencia · Admin</p>
        <LockKeyhole className="w-6 h-6 text-ink-mute mx-auto mb-4" strokeWidth={1.4} />
        <h1
          className="font-display text-[2rem] text-ink"
          style={{ fontFamily: '"Instrument Serif", serif' }}
        >
          Panel administrativo
        </h1>
        <p className="mt-3 font-editorial italic text-[1rem] text-ink-soft">
          Ingresa la contraseña administrativa.
        </p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Contraseña"
          className="mt-8 w-full bg-transparent border-b border-line focus:border-latir-red outline-none text-center font-ui text-[1rem] py-3 tracking-[0.1em]"
          data-testid="admin-home-password"
        />
        {error && (
          <p
            className="mt-3 font-ui uppercase text-[0.68rem] tracking-[0.2em] text-latir-red"
            data-testid="admin-home-login-error"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || !password}
          className="mt-8 inline-flex items-center gap-3 bg-ink text-paper-raised px-6 py-3 font-ui uppercase tracking-[0.2em] text-[0.72rem] hover:bg-latir-red transition-colors disabled:opacity-50"
          data-testid="admin-home-login-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
        </button>
      </form>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard({ token }) {
  const [soldCount, setSoldCount] = useState(null);
  const [visitsSummary, setVisitsSummary] = useState(null); // { open_slots, total_bookings, pending_groups }

  useEffect(() => {
    // Sold works — public endpoint, cheap.
    fetch(`${API}/api/artwork-status`)
      .then((r) => r.json())
      .then((j) => setSoldCount((j.sold || []).length))
      .catch(() => setSoldCount(null));

    // Visits summary — admin endpoints.
    const auth = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/api/visits/admin/slots`, { headers: auth }).then((r) => r.json()),
      fetch(`${API}/api/visits/admin/bookings/pending`, { headers: auth }).then((r) => r.json()),
    ])
      .then(([slotsRes, pendingRes]) => {
        const slots = slotsRes.slots || slotsRes || [];
        const openSlots = slots.filter((s) => s.is_open !== false).length;
        const totalBookings = slots.reduce(
          (acc, s) => acc + (s.booked || s.occupied || 0),
          0
        );
        const pending = (pendingRes.pending || pendingRes || []).length;
        setVisitsSummary({
          open_slots: openSlots,
          total_slots: slots.length,
          total_bookings: totalBookings,
          pending_groups: pending,
        });
      })
      .catch(() => setVisitsSummary(null));
  }, [token]);

  return (
    <div className="paper-grain min-h-screen bg-paper" data-testid="admin-home-dashboard">
      <AdminHeader />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <p className="eyebrow text-latir-red">Arteconciencia · LATIR</p>
        <h1
          className="font-display text-[2.4rem] text-ink mt-1"
          style={{ fontFamily: '"Instrument Serif", serif' }}
        >
          Panel administrativo
        </h1>
        <p className="mt-2 font-editorial italic text-[1rem] text-ink-soft">
          Gestioná las obras vendidas y las reservas de visitas guiadas desde un solo lugar.
        </p>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <AdminCard
            to="/admin/obras"
            icon={<Palette className="w-6 h-6 text-latir-red" strokeWidth={1.4} />}
            title="Estado de venta de obras"
            description="Marcar y desmarcar obras como VENDIDAS. Se actualiza al momento en la web pública."
            stats={[
              { label: "Obras totales", value: artists.length },
              {
                label: "Marcadas como vendidas",
                value: soldCount == null ? "…" : soldCount,
              },
            ]}
            testId="admin-card-obras"
          />

          <AdminCard
            to="/admin/visitas"
            icon={<CalendarCheck className="w-6 h-6 text-latir-red" strokeWidth={1.4} />}
            title="Reservas de visitas guiadas"
            description="Consultar y gestionar slots, reservas individuales y grupos pendientes de aprobación."
            stats={
              visitsSummary
                ? [
                    { label: "Slots abiertos", value: `${visitsSummary.open_slots} / ${visitsSummary.total_slots}` },
                    { label: "Personas inscritas", value: visitsSummary.total_bookings },
                    { label: "Grupos pendientes", value: visitsSummary.pending_groups },
                  ]
                : [{ label: "Cargando…", value: "…" }]
            }
            testId="admin-card-visitas"
          />
        </div>

        <p className="mt-12 font-ui text-[0.7rem] tracking-[0.15em] text-ink-mute leading-relaxed">
          Los cambios se guardan de inmediato en la base de datos de producción. No es necesario volver a publicar la web para reflejar ventas o reservas.
        </p>
      </div>
    </div>
  );
}

function AdminCard({ to, icon, title, description, stats, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group block bg-paper-raised border border-line p-6 sm:p-8 rounded-[3px] hover:border-latir-red transition-colors"
    >
      <div className="flex items-center justify-between mb-4">
        {icon}
        <ArrowRight
          className="w-4 h-4 text-ink-soft group-hover:text-latir-red group-hover:translate-x-1 transition-all"
          strokeWidth={1.4}
        />
      </div>
      <h2
        className="font-display text-[1.4rem] text-ink"
        style={{ fontFamily: '"Instrument Serif", serif' }}
      >
        {title}
      </h2>
      <p className="mt-2 font-editorial italic text-[0.95rem] text-ink-soft leading-relaxed">
        {description}
      </p>
      <div className="mt-6 grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="border-t border-line pt-3">
            <p className="font-ui uppercase tracking-[0.15em] text-[0.58rem] text-ink-mute">
              {s.label}
            </p>
            <p
              className="mt-1 font-display text-[1.4rem] text-ink"
              style={{ fontFamily: '"Instrument Serif", serif' }}
            >
              {s.value}
            </p>
          </div>
        ))}
      </div>
    </Link>
  );
}
